import { cache } from 'react'
import { SearchResponse } from 'algoliasearch/lite'
import { InitialResults } from 'instantsearch.js'
import { searchClient } from 'algolia/searchClient'
import { FacetConfiguration } from 'components/commercetools-ui/organisms/product/product-list-algolia/types'
import { FacetValuesByAttribute } from 'helpers/algolia/parsePlFacetUrlParams'
import { getCachedContentstackAlgoliaFacetsConfiguration } from 'helpers/contentstack/cachedContentStackData'
import { facetTemplateCurrencyCode, getLocalizationInfo } from 'project.config'
import { getIndex, SupportedLocale } from 'providers/algolia/localized-index/indexMap'
import { logError } from 'telemetry/server'
import { ComponentDataProvider } from '../../registry/DataProviderRegistry'

interface AlgoliaQueryBuilderDataProvider {
  products: InitialResults
  facetValues: FacetValuesByAttribute
  facetsConfiguration: Record<string, FacetConfiguration>
}

export interface ContentstackQueryBuilder {
  filterQueries: { filterKey: string, filterValue: string }[]
  filterType: { label: string, value: string }
  entityType: { label: string, value: string }
}

const fetchProductsAndFacetValuesFromAlgoliaWithFilterQueries = cache(async (filterQueries: ContentstackQueryBuilder['filterQueries'], indexName: string, locale: SupportedLocale): Promise<{ products: InitialResults, facetValues: FacetValuesByAttribute, facetsConfiguration: Record<string, FacetConfiguration> }> => {
  try {
    const { currency } = getLocalizationInfo(locale)
    const filters = getParsedFilterQueries(filterQueries, locale)

    // Match UI Configure: hitsPerPage / maxValuesPerFacet
    const hitsPerPage = 24
    const maxValuesPerFacet = 1000

    // Load facetsConfiguration first so we request the same facet list as the client Configure
    const facetsConfiguration = await getCachedContentstackAlgoliaFacetsConfiguration('PLP', locale, currency)
    const facetKeys = Object.keys(facetsConfiguration)
    const disjunctiveFacets = facetKeys.filter((key) => facetsConfiguration[key]?.valueOperator !== 'and')
    const conjunctiveFacets = facetKeys.filter((key) => facetsConfiguration[key]?.valueOperator === 'and')

    const response = await searchClient.search([{
      indexName,
      params: {
        filters,
        facets: facetKeys.length > 0 ? facetKeys : ['*'],
        maxValuesPerFacet,
        hitsPerPage,
        analytics: true,
        clickAnalytics: true,
        attributesToRetrieve: [
          'objectID',
          'name',
          'slug',
          'key',
          'categories',
          'isLoyaltyProduct',
          'reviewStatistics',
          'attributes',
          'variants',
        ],
        attributesToHighlight: [],
      },
    }])

    const results = (response.results?.[0] || {}) as SearchResponse<unknown>

    const facetValues: FacetValuesByAttribute = {}
    if (results.facets) {
      Object.entries(results.facets).forEach(([attribute, facetData]) => {
        facetValues[attribute] = Object.keys(facetData as Record<string, number>)
      })
    }

    // InstantSearchSSRProvider expects InitialResults = Record<indexName, { state, results }>
    // state must match UI Configure (filters, hitsPerPage, maxValuesPerFacet, disjunctiveFacets) for SSR/client consistency
    const products: InitialResults = {
      [indexName]: {
        state: {
          filters,
          hitsPerPage,
          maxValuesPerFacet,
          ...(disjunctiveFacets.length > 0 && { disjunctiveFacets }),
          ...(conjunctiveFacets.length > 0 && { facets: conjunctiveFacets }),
        },
        results: [results] as InitialResults[string]['results'],
      },
    }

    return {
      products,
      facetValues,
      facetsConfiguration,
    }

  } catch (error) {
    logError(error, { message: 'Error loading Products from Algolia', meta: { component: 'AlgoliaQueryBuilderDataProvider', fn: 'fetchProductsAndFacetValuesFromAlgoliaWithFilterQueries' } })
    return { products: {}, facetValues: {}, facetsConfiguration: {} }
  }
})

const loadProducts = async (contentItem: ContentstackAlgoliaQueryBuilder, indexName: string, locale: SupportedLocale): Promise<{ products: InitialResults, facetValues: FacetValuesByAttribute, facetsConfiguration: Record<string, FacetConfiguration> }> => {
  if (contentItem.algolia_filters) {
    if ('filterQueries' in contentItem.algolia_filters && (contentItem.algolia_filters.filterQueries as []).length > 0) {
      return await fetchProductsAndFacetValuesFromAlgoliaWithFilterQueries((contentItem.algolia_filters as unknown as ContentstackQueryBuilder).filterQueries, indexName, locale)
    }
  }

  return { products: {}, facetValues: {}, facetsConfiguration: {} }
}

const load = async (contentItem: ContentstackAlgoliaQueryBuilder, locale: SupportedLocale): Promise<AlgoliaQueryBuilderDataProvider> => {
  const indexName = getIndex('products', locale as 'de-DE' | 'de-AT' | 'cs-CZ')

  try {
    return await loadProducts(contentItem, indexName, locale)
  } catch (error) {
    logError(error, { message: 'Error loading ProductSlider data', meta: { component: 'AlgoliaQueryBuilderDataProvider', fn: 'load' } })
    return { products: {}, facetValues: {}, facetsConfiguration: {} }
  }
}

export const algoliaQueryBuilderDataProvider: ComponentDataProvider<ContentstackAlgoliaQueryBuilder, AlgoliaQueryBuilderDataProvider> = {
  contentType: 'algoliaquerybuilder',
  load,
}

export const getParsedFilterQueries = (filterQueries: ContentstackQueryBuilder['filterQueries'], locale: SupportedLocale): string => {
  const { currency } = getLocalizationInfo(locale)
  const facetCurrency = facetTemplateCurrencyCode(currency)
  return filterQueries.map((fq) => {
    const parsedFilterKey = fq.filterKey.replace(/\{currency\}/, facetCurrency).replace(/\{locale\}/, locale)
    return `${parsedFilterKey}:"${fq.filterValue}"`
  })?.join(' AND ')
}
